import _extends from '@babel/runtime/helpers/extends';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import * as React from 'react';
import { Box } from '../Box/Box.js';
import { ScreenReaderOnly } from '../ScreenReaderOnly/ScreenReaderOnly.js';
import { Typography } from '../Typography/Typography.js';

var _excluded = ["score", "color", "size", "layout", "scorePosition", "align", "scoreDescription", "description", "headingTag", "srOnlyText"];
var ScoreTextVariant = {
  s: 'body-s-strong',
  m: 'body-default-strong',
  l: 'heading-l'
};
var DescriptionTextVariant = {
  s: 'caption-default',
  m: 'body-s',
  l: 'body-s'
};
var ScoreTextColor = {
  primary: 'product.primary',
  secondary: 'deprecated.secondary.interactive'
};

/**
 * Review is used to show cumulative property, location and facilities score based on guests rating and reviews.
 */
var Review = /*#__PURE__*/React.memo(function (props) {
  var score = props.score,
    color = props.color,
    size = props.size,
    layout = props.layout,
    scorePosition = props.scorePosition,
    align = props.align,
    scoreDescription = props.scoreDescription,
    description = props.description,
    headingTag = props.headingTag,
    srOnlyText = props.srOnlyText,
    rest = _objectWithoutProperties(props, _excluded);
  var parentRef = React.useRef(null);
  var reviewScoreRef = React.useRef(null);
  var _React$useState = React.useState(true),
    _React$useState2 = _slicedToArray(_React$useState, 2),
    showDescription = _React$useState2[0],
    setShowDescription = _React$useState2[1];
  var scoreElement = React.useMemo(function () {
    return score && /*#__PURE__*/React.createElement(Typography, {
      textColor: ScoreTextColor[color],
      variant: ScoreTextVariant[size],
      tag: headingTag || 'p',
      display: "inline-flex",
      "aria-hidden": srOnlyText ? true : undefined
    }, scorePosition === 'leading' && /*#__PURE__*/React.createElement(Typography, {
      tag: "span",
      me: scoreDescription ? '4' : '0'
    }, score), scoreDescription && /*#__PURE__*/React.createElement(Typography, {
      tag: "span",
      me: scorePosition === 'trailing' ? '4' : '0'
    }, scoreDescription), scorePosition === 'trailing' && score);
  }, [score, color, size, headingTag, srOnlyText, scorePosition, scoreDescription]);
  var descriptionElement = React.useMemo(function () {
    return description && /*#__PURE__*/React.createElement(Typography, {
      textColor: "base.subtle",
      variant: DescriptionTextVariant[size],
      ms: layout === 'row' ? '4' : '0',
      mt: layout === 'column' ? '4' : '0',
      "aria-hidden": srOnlyText ? true : undefined,
      lineClamp: 1,
      tag: "p"
    }, description);
  }, [description, layout, size, srOnlyText]);
  var alignItem = React.useMemo(function () {
    return layout === 'column' && align !== 'end' ? 'baseline' : 'flex-end';
  }, [align, layout]);
  var justifyContent = React.useMemo(function () {
    return layout === 'row' && align === 'end' ? 'flex-end' : undefined;
  }, [align, layout]);
  React.useLayoutEffect(function () {
    var reviewScoreElement = reviewScoreRef.current;
    var parentElement = parentRef.current;
    // If review score overflowed through parent component, hide additional description
    if (reviewScoreElement && parentElement && parentElement.clientWidth < reviewScoreElement.clientWidth) {
      setShowDescription(false);
    } else {
      setShowDescription(true);
    }
  }, [score, scoreDescription, description]);
  return /*#__PURE__*/React.createElement(Box, _extends({
    display: "flex",
    alignItems: alignItem,
    flexDirection: layout,
    justifyContent: justifyContent,
    ref: parentRef
  }, rest), srOnlyText && /*#__PURE__*/React.createElement(ScreenReaderOnly, null, srOnlyText), /*#__PURE__*/React.createElement(Box, {
    display: "flex",
    flexDirection: "row",
    alignSelf: align === 'start' ? 'flex-start' : undefined,
    alignItems: "baseline"
  }, /*#__PURE__*/React.createElement(Box, {
    display: "flex",
    flexDirection: "row",
    ref: reviewScoreRef
  }, scoreElement), layout === 'row' && showDescription && descriptionElement), layout === 'column' && descriptionElement);
});
Review.displayName = 'Review';
Review.defaultProps = {
  color: 'primary',
  size: 'm',
  layout: 'row',
  scorePosition: 'leading',
  align: 'start'
};

export { Review };
//# sourceMappingURL=Review.js.map
